import styles from './update-photo-wrapper.module.css';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { requiredEnum } from '@/util/validators';
import { Form } from '@/components/form';
import { PhotoDetailsModal } from '@/components/uploads/photo-upload/photo-details-modal';
import {
  BodyLocationType,
  FileReasonType,
  FileStatus,
  ImageFileDto,
  ReplaceImageErrorType,
} from '@/api/api.schemas';
import { useEncounter } from '@/contexts/encounter-context';
import { useTranslation } from '@/contexts/i18n-context';
import { useAsyncAction } from '@/hooks/use-async-action';
import { FileInput } from '../file-input';
import clsx from 'clsx';
import { useReplaceEncountersImages } from '@/api/replace-image';
import { Button } from '@/components/button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { PreviewImageButton } from './preview-image-button';
import { Banner } from '@/components/banner';
import { Img } from '@/components/img';

const translationBase = 'visit.photoUpload';
const updatePhotosTranslationBase = `${translationBase}.updatePhotos`;

const photoSchema = z.object({
  bodyLocation: requiredEnum(BodyLocationType),
  description: z.string().optional(),
});

type PhotoFormValues = z.infer<typeof photoSchema>;

interface UpdateImageWrapperProps {
  photo: ImageFileDto;
  alt: string;
  onUpdate: () => void;
}

export function UpdateImageWrapper({ photo, alt, onUpdate }: UpdateImageWrapperProps) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const isMobile = useMediaQuery('--screen-md');
  const { mutateAsync: replaceImageAsync } = useReplaceEncountersImages();

  const [error, setError] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);
  const [image, setImage] = useState<File>();

  const handleFilesSelected = (files: File[]) => {
    setImage(files[0]);
    setError(undefined);
    setIsOpen(true);
  };

  const form = useForm<PhotoFormValues>({
    resolver: zodResolver(photoSchema),
    mode: 'onBlur',
    defaultValues: {
      bodyLocation: photo.bodyLocation ?? undefined,
      description: photo.description,
    },
  });

  const { trigger: updatePhoto, loading: isUpdatingPhoto } = useAsyncAction(
    async (data: PhotoFormValues) => {
      if (!image) return;

      await replaceImageAsync(
        {
          encounterId: encounter.id,
          imageId: photo.id,
          data: {
            file: image,
            bodyLocation: data.bodyLocation,
            description: data.description,
          },
        },
        {
          onError: (error) => {
            setError(() => {
              switch (error.type) {
                case ReplaceImageErrorType.ValidationFailed:
                case ReplaceImageErrorType.InvalidImage:
                  return t(`${updatePhotosTranslationBase}.errors.fileTypeNotSupported`);
                default:
                  return t('common.errors.unknown');
              }
            });
            setIsOpen(false);
          },
        },
      );

      onUpdate();
      setIsOpen(false);
    },
  );

  const onSubmit = useCallback(
    (data: PhotoFormValues) => {
      updatePhoto(data);
    },
    [updatePhoto],
  );

  const isPhotoBad = photo.status === FileStatus.Bad;
  const isPhotoReplaced = photo.status === FileStatus.Replaced;

  return (
    <div className={styles.updatePhotoWrapper}>
      {error && (
        <Banner type="error" focusOnMount>
          {error}
        </Banner>
      )}

      <div className={styles.photoContainer}>
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <PreviewImageButton photo={photo} alt={alt} />
            <Img src={photo.imageSrc} alt={alt} className={styles.img} />
          </div>

          <div className={styles.photoDetails}>
            {photo.bodyLocation && <p className="h4">{t(`bodyLocation.${photo.bodyLocation}`)}</p>}
            {photo.description && (
              <p className={clsx(styles.description, 'body-3')}>{photo.description}</p>
            )}
          </div>
        </div>
        {isPhotoBad && (
          <FileInput
            onFilesSelected={handleFilesSelected}
            buttonProps={{
              children: t(`${updatePhotosTranslationBase}.replaceBtn`),
              className: styles.replaceBtn,
              size: isMobile ? 'M' : 'L',
              loading: isUpdatingPhoto,
            }}
          />
        )}

        {isPhotoReplaced && (
          <Button
            type="button"
            size={isMobile ? 'M' : 'L'}
            variant="outlined"
            disabled
            leftIcon="check"
            className={styles.replaceBtn}
          >
            {t(`${updatePhotosTranslationBase}.replacedBtn`)}
          </Button>
        )}
      </div>

      {isPhotoBad && (
        <div className={styles.reasonContainer}>
          <p className="semibold">{t(`${updatePhotosTranslationBase}.reasons.title`)}</p>
          <span>
            {photo.reasonType &&
              (photo.reasonType === FileReasonType.Other && photo.otherReason ? (
                <p>{photo.otherReason}</p>
              ) : (
                <p>{t(`${updatePhotosTranslationBase}.reasons.${photo.reasonType}`)}</p>
              ))}
          </span>
        </div>
      )}

      {image && (
        <Form form={form} onSubmit={onSubmit}>
          <PhotoDetailsModal
            photo={{
              file: image,
              imageSrc: URL.createObjectURL(image),
              bodyLocation: photo.bodyLocation,
              description: photo.description,
            }}
            isOpen={isOpen}
            alt={alt}
            onClose={() => {
              setIsOpen(false);
              setImage(undefined);
            }}
            handleSubmit={form.handleSubmit(onSubmit)}
            disableSelector={!!photo.bodyLocation}
            submitButtonTxt={t(`${updatePhotosTranslationBase}.submitModalButton`)}
            isPending={isUpdatingPhoto}
          />
        </Form>
      )}
    </div>
  );
}
